import { text } from "@quickpoint.me/shared";
import { useEffect, useRef } from "react";
import styled from "styled-components";

/* keyboard-shortcuts overlay, opened with "?" — the convention shared by
   reveal.js, Gmail and GitHub. A reference card, deliberately separate from
   the Settings panel (state lives there, this is documentation). */

const Backdrop = styled.div`
	position: fixed;
	inset: 0;
	background: rgba(0, 0, 0, 0.45);
	display: grid;
	place-items: center;
	z-index: 120;
`;

const Card = styled.div`
	background: #fffff8;
	color: #111;
	border-radius: 8px;
	padding: 1.4rem 1.8rem;
	max-width: min(92vw, 26rem);
	max-height: 85vh;
	overflow: auto;
	font-family: Avantt-Regular, sans-serif;

	h2 {
		margin: 0 0 0.8rem;
		font-size: 1.1rem;
	}

	dl {
		display: grid;
		grid-template-columns: 1fr auto;
		gap: 0.45rem 1.4rem;
		margin: 0 0 1.2rem;
	}

	dt {
		margin: 0;
	}

	dd {
		margin: 0;
		text-align: end;
		font-variant-numeric: tabular-nums;
		color: #333;
	}
`;

const CloseButton = styled.button`
	display: block;
	margin-inline-start: auto;
	min-height: 44px;
	padding: 0 1.2rem;
	border: 1px solid #767676;
	border-radius: 8px;
	background: transparent;
	font: inherit;
	cursor: pointer;

	&:focus-visible {
		outline: 3px solid #4a90d9;
		outline-offset: 2px;
	}
`;

const ROWS: [string, string][] = [
	[text.reader.shortcutsNext, text.reader.shortcutsNextKeys],
	[text.reader.shortcutsPrevious, text.reader.shortcutsPreviousKeys],
	[text.reader.shortcutsFirstLast, text.reader.shortcutsFirstLastKeys],
	[text.reader.shortcutsHelp, text.reader.shortcutsHelpKeys],
	[text.reader.shortcutsSettings, text.reader.shortcutsSettingsKeys],
];

function KeyboardHelp({ onClose }: { onClose: () => void }) {
	const closeRef = useRef<HTMLButtonElement>(null);
	const restoreRef = useRef<HTMLElement | null>(null);

	useEffect(() => {
		restoreRef.current = document.activeElement as HTMLElement | null;
		closeRef.current?.focus();
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				e.stopPropagation();
				onClose();
			}
			// single focusable control, so trap is trivial
			if (e.key === "Tab") {
				e.preventDefault();
				closeRef.current?.focus();
			}
		};
		window.addEventListener("keydown", onKey, true);
		return () => {
			window.removeEventListener("keydown", onKey, true);
			restoreRef.current?.focus();
		};
	}, [onClose]);

	return (
		<Backdrop onClick={onClose}>
			<Card
				role="dialog"
				aria-modal="true"
				aria-labelledby="qp-shortcuts-title"
				onClick={(e) => e.stopPropagation()}
			>
				<h2 id="qp-shortcuts-title">{text.reader.shortcutsTitle}</h2>
				<dl>
					{ROWS.map(([what, keys]) => (
						<div key={what} style={{ display: "contents" }}>
							<dt>{what}</dt>
							<dd>{keys}</dd>
						</div>
					))}
				</dl>
				<CloseButton ref={closeRef} type="button" onClick={onClose}>
					{text.reader.shortcutsClose}
				</CloseButton>
			</Card>
		</Backdrop>
	);
}

export default KeyboardHelp;
